// components/entities/grids/ProjectsGrid.tsx
'use client';

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import type { LeasingProjectFilters, ProjectType, QuickMenuAction } from '#types/types';
import { Frame, Rows3 } from 'lucide-react';
import { STUDIO_PROJECT_KINDS } from '#lib/studio-project-view';
import clsx from 'clsx';
import { createScopedOrgMemberToolbarExtras } from '@/components/entities/tables/ScopedOrgMemberToolbarExtras';
import { Grid } from '@/components/portal/Grid';
import { ListFactoryToggle } from '@/components/marketing/ListFactoryToggle';
import { EntityView } from '../EntityView';
import { isUrwProjectDashboardAdmin, URW_ORGANISATION_ID } from '#lib/urwProjectDashboardAccess';
import ArtboardSizeDialog from '@/components/vector/ArtboardSizeDialog';
import type { ArtboardSize } from '#lib/vector-artboards';

const translations = {
  en: {
    projects: 'All projects',
    pageTitle: 'Projects',
    breadcrumb: 'Studio / Projects',
    newProject: 'New project',
    canvasProject: 'Canvas project',
    listProject: 'List project',
    filterLabel: 'Filter projects',
    filterAll: 'Active',
    filterTrash: 'Trash',
    teamProjects: 'URW team projects',
    myProjects: 'My projects',
    sortOptions: { updated: 'Last updated', created: 'Date created', name: 'Name', credits: 'Credits spent' },
  },
  fr: {
    projects: 'Tous les projets',
    pageTitle: 'Projets',
    breadcrumb: 'Studio / Projets',
    newProject: 'Nouveau projet',
    canvasProject: 'Projet Canvas',
    listProject: 'Projet Liste',
    filterLabel: 'Filtrer les projets',
    filterAll: 'Actifs',
    filterTrash: 'Corbeille',
    teamProjects: 'Projets equipe URW',
    myProjects: 'Mes projets',
    sortOptions: { updated: 'Derniere modif.', created: 'Date creation', name: 'Nom', credits: 'Credits utilises' },
  },
};

const renderProjectCard = (project: { id: string }) => (
  <EntityView key={project.id} type="Project" entityId={project.id} variant="card" />
);

interface ProjectsGridProps {
  initialFilters?: Partial<LeasingProjectFilters>;
  lockedFilters?: Partial<LeasingProjectFilters>;
  assetLedgerTitle?: string;
  assetLedgerBreadcrumb?: string;
  showDefaultToolbarSelectors?: boolean;
  renderCustomToolbarHeader?: () => React.ReactNode;
}

const ProjectsGrid = memo(({
  initialFilters,
  lockedFilters,
  assetLedgerTitle,
  assetLedgerBreadcrumb,
  showDefaultToolbarSelectors = true,
  renderCustomToolbarHeader,
}: ProjectsGridProps) => {
  const { kernel } = useKernel();
  const userContext = useKernelStore((state) => state.userContext);
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const orgRegistry = useKernelStore((state) => state.inventory.Organisation.registry);
  const userRegistry = useKernelStore((state) => state.inventory.User.registry);

  const currentOrg = userContext.organisationId ? orgRegistry[userContext.organisationId] : null;
  const currentUser = userContext.dbUserId ? userRegistry[userContext.dbUserId] : null;
  const isOrganisationLocked = lockedFilters?.organisationId !== undefined;
  const isUserLocked = lockedFilters?.userId !== undefined;
  const isTrashLocked = lockedFilters?.toDelete !== undefined;
  const hasDefaultToolbarSelectors = showDefaultToolbarSelectors && ((userContext.isFaterAdmin && !isOrganisationLocked) || !isUserLocked);
  const canToggleUrwTeamProjects = !isUserLocked && isUrwProjectDashboardAdmin({
    userContext,
    user: currentUser,
    organisation: currentOrg,
  });

  const baseFilters = useMemo(() => {
    const base: Record<string, unknown> = { sortBy: 'UPDATED', sortDir: 'desc', ...initialFilters };
    if (!userContext.isFaterAdmin && currentOrg?.id && base.organisationId === undefined) {
      base.organisationId = currentOrg.id;
    }
    return base;
  }, [currentOrg?.id, initialFilters, userContext.isFaterAdmin]);

  const sortOptions = useMemo(() => [
    { label: t.sortOptions.updated, field: 'UPDATED' },
    { label: t.sortOptions.created, field: 'CREATED' },
    { label: t.sortOptions.name, field: 'NAME' },
    { label: t.sortOptions.credits, field: 'CREDITS' },
  ], [t]);

  const startProject = useCallback(async (projectType: ProjectType, artboard?: ArtboardSize) => {
    if (!userContext.dbUserId) return;
    await kernel.current?.createProjectAndQuickstart(userContext.dbUserId, '', [], { projectType, artboard });
  }, [kernel, userContext.dbUserId]);
  // A Vector project starts from its artboard size, as Illustrator's New Document does.
  const [artboardDialogOpen, setArtboardDialogOpen] = useState(false);
  const closeArtboardDialog = useCallback(() => setArtboardDialogOpen(false), []);
  const pickArtboard = useCallback((size: ArtboardSize) => {
    setArtboardDialogOpen(false);
    void startProject('VECTOR', size);
  }, [startProject]);

  // The project KIND is chosen at creation and fixed for life (Canvas and List
  // are not interoperable), so "New Project" opens a two-option chooser.
  const handleCreateProject = useCallback((event?: React.MouseEvent) => {
    if (!userContext.dbUserId) return;
    if (lockedFilters?.projectKind === 'vector') {
      setArtboardDialogOpen(true);
      return;
    }
    if (lockedFilters?.projectKind) {
      void startProject(STUDIO_PROJECT_KINDS[lockedFilters.projectKind].defaultType);
      return;
    }
    const x = event?.clientX ?? (typeof window !== 'undefined' ? window.innerWidth / 2 - 110 : 200);
    const y = event?.clientY ?? (typeof window !== 'undefined' ? window.innerHeight / 2 : 200);
    const actions: QuickMenuAction[] = [
      {
        label: t.canvasProject,
        icon: <Frame size={14} className="text-white/60" />,
        onClick: () => { void startProject('FREEFORM'); },
      },
      {
        label: t.listProject,
        icon: <Rows3 size={14} className="text-white/60" />,
        onClick: () => { void startProject('LIST'); },
      },
    ];
    kernel.current?.setMenuData({ position: { x, y }, actions });
  }, [kernel, lockedFilters?.projectKind, startProject, t, userContext.dbUserId]);

  const renderToolbarExtras = useCallback((filters: Record<string, unknown>, setFilters: (f: Record<string, unknown>) => void) => {
    if (!hasDefaultToolbarSelectors && !renderCustomToolbarHeader) return null;

    const selectedOrgId = filters.organisationId ? String(filters.organisationId) : null;
    const selectedUser = filters.userId ? userRegistry[String(filters.userId)] : null;

    const handleOrgSelect = (orgId: string | null) => {
      setFilters({ ...filters, organisationId: orgId ?? undefined, userId: undefined });
    };

    const handleUserSelect = (user: { id?: string } | null) => {
      setFilters({ ...filters, userId: user?.id });
    };

    const defaultToolbarExtras = createScopedOrgMemberToolbarExtras({
      renderCustomToolbarHeader,
      hasDefaultToolbarSelectors,
      showOrganisationSelector: userContext.isFaterAdmin && !isOrganisationLocked,
      showUserSelector: !isUserLocked,
      selectedOrgId,
      selectedUser,
      activeOrgId: String(filters.organisationId || currentOrg?.id || ''),
      onOrgSelect: handleOrgSelect,
      onUserSelect: handleUserSelect,
    })?.() ?? null;

    if (!canToggleUrwTeamProjects) return defaultToolbarExtras;

    const isShowingTeamProjects = !filters.userId && String(filters.organisationId || currentOrg?.id || '') === URW_ORGANISATION_ID;
    const handleTeamProjectsToggle = () => {
      setFilters({
        ...filters,
        organisationId: currentOrg?.id || URW_ORGANISATION_ID,
        userId: isShowingTeamProjects ? userContext.dbUserId || undefined : undefined,
      });
    };

    return (
      <>
        <button
          type="button"
          onClick={handleTeamProjectsToggle}
          className={clsx(
            'inline-flex h-9 items-center gap-1.5 rounded-full px-3 transition-colors',
            isShowingTeamProjects
              ? 'bg-white/[0.08] text-white'
              : 'text-[#E5E5E5] hover:bg-white/[0.08] hover:text-white'
          )}
          style={{ fontSize: 14 }}
          title={isShowingTeamProjects ? t.myProjects : t.teamProjects}
        >
          <span>{isShowingTeamProjects ? t.myProjects : t.teamProjects}</span>
        </button>
        {defaultToolbarExtras}
      </>
    );
  }, [
    canToggleUrwTeamProjects,
    currentOrg,
    hasDefaultToolbarSelectors,
    isOrganisationLocked,
    isUserLocked,
    renderCustomToolbarHeader,
    t.myProjects,
    t.teamProjects,
    userContext,
    userRegistry,
  ]);

  // ChatGPT's view chips (the Source dashboard's own), not a funnel hiding a two-item menu.
  const renderCenterToolbarContent = useCallback((filters: Record<string, unknown>, setFilters: (f: Record<string, unknown>) => void) => {
    if (isTrashLocked) return null;
    const isTrashView = filters.toDelete === true;
    const chip = (active: boolean) => clsx(
      'h-8 rounded-full px-3 transition-colors',
      active ? 'bg-white/[0.08] text-white' : 'text-[#E5E5E5] hover:bg-white/[0.05]',
    );
    return (
      <div className="flex items-center gap-1" role="group" aria-label={t.filterLabel}>
        <button type="button" aria-pressed={!isTrashView} onClick={() => setFilters({ ...filters, toDelete: undefined })} className={chip(!isTrashView)} style={{ fontSize: 14 }}>
          {t.filterAll}
        </button>
        <button type="button" aria-pressed={isTrashView} onClick={() => setFilters({ ...filters, toDelete: true })} className={chip(isTrashView)} style={{ fontSize: 14 }}>
          {t.filterTrash}
        </button>
      </div>
    );
  }, [isTrashLocked, t.filterAll, t.filterLabel, t.filterTrash]);

  return (
    <>
    <Grid
      entityType="Project"
      pageSize={16}
      initialFilters={baseFilters}
      lockedFilters={lockedFilters}
      embedded={lockedFilters?.projectKind ? false : undefined}
      sortOptions={sortOptions}
      assetLedgerTitle={assetLedgerTitle || t.projects}
      assetLedgerBreadcrumb={assetLedgerBreadcrumb || t.breadcrumb}
      searchPlaceholder={lang === 'fr' ? 'Rechercher des projets' : 'Search projects'}
      onCreateClick={handleCreateProject}
      createButtonLabel={t.newProject}
      viewSwitch={lockedFilters?.projectKind === 'list' ? <ListFactoryToggle /> : undefined}
      renderToolbarExtras={renderToolbarExtras}
      renderCenterToolbarContent={renderCenterToolbarContent}
      renderCard={renderProjectCard}
      // The module names itself, as every module's home does (owner, 2026-09-29).
      pageTitle={lockedFilters?.projectKind ? `Studio ${STUDIO_PROJECT_KINDS[lockedFilters.projectKind].label}` : t.pageTitle}
      gridClass="grid-cols-2 gap-y-7 lg:grid-cols-3 xl:grid-cols-4"
    />
    {artboardDialogOpen && <ArtboardSizeDialog title="New vector project" confirmLabel="Create" onPick={pickArtboard} onClose={closeArtboardDialog} />}
    </>
  );
});

ProjectsGrid.displayName = 'ProjectsGrid';
export { ProjectsGrid };
